import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getReaderReadingHistory } from '../services/readerService';
import { updateReadingEntry, removeChapterRead } from '../services/readingService';
import { getFromStorage, STORAGE_KEYS } from '../utils/storageUtils';

function ReadingReports() {
  const [readingHistory, setReadingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedReader, setSelectedReader] = useState('');
  const [selectedReaderName, setSelectedReaderName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'read_date', direction: 'desc' });
  const [editingEntry, setEditingEntry] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDate, setEditDate] = useState('');
  const [viewingNotes, setViewingNotes] = useState(null);
  const navigate = useNavigate();
  const { readerId } = useParams();

  // Initialize selected reader from URL param or localStorage
  useEffect(() => {
    if (readerId) {
      setSelectedReader(readerId);
    } else {
      const savedReader = getFromStorage(STORAGE_KEYS.SELECTED_READER);
      if (savedReader) {
        setSelectedReader(savedReader);
      }
    }
  }, [readerId]);

  // Listen for reader changes from the ReaderSelector component
  useEffect(() => {
    const handleReaderChange = (event) => {
      const { readerId, readerName } = event.detail;
      setSelectedReader(readerId);
      setSelectedReaderName(readerName);
    };
    
    window.addEventListener('readerChanged', handleReaderChange);
    
    return () => {
      window.removeEventListener('readerChanged', handleReaderChange);
    };
  }, []);

  // Fetch reading history when a reader is selected
  useEffect(() => {
    if (!selectedReader) return;
    
    const fetchReadingHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getReaderReadingHistory(selectedReader);
        
        // Sort the reading history based on the current sort configuration
        const sortedHistory = sortReadingHistory(response.chaptersRead || []);
        setReadingHistory(sortedHistory);
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch reading history:', err);
        setError('Failed to load reading history. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchReadingHistory();
  }, [selectedReader]);

  // Sort reading history based on the current sort configuration
  const sortReadingHistory = (history) => {
    const sortableHistory = [...history];
    
    sortableHistory.sort((a, b) => {
      if (sortConfig.key === 'read_date') {
        const dateA = new Date(a.read_date || a.readDate);
        const dateB = new Date(b.read_date || b.readDate);
        
        if (sortConfig.direction === 'asc') {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      } else if (sortConfig.key === 'book') {
        const bookA = a.book.toUpperCase();
        const bookB = b.book.toUpperCase();
        
        if (sortConfig.direction === 'asc') {
          return bookA.localeCompare(bookB);
        } else {
          return bookB.localeCompare(bookA);
        }
      } else if (sortConfig.key === 'chapter') {
        const chapterA = parseInt(a.chapter);
        const chapterB = parseInt(b.chapter);
        
        if (sortConfig.direction === 'asc') {
          return chapterA - chapterB;
        } else {
          return chapterB - chapterA;
        }
      }
      
      return 0;
    });
    
    return sortableHistory;
  };

  // Handle sorting when a column header is clicked
  const handleSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
    
    // Re-sort the reading history
    setReadingHistory(sortReadingHistory(readingHistory));
  };

  // Get the sort direction indicator for a column
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Format a date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format a date for the date input field
  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Handle edit button click
  const handleEditClick = (entry) => {
    setEditingEntry(entry);
    setEditNotes(entry.notes || '');
    setEditDate(formatDateForInput(entry.read_date || entry.readDate));
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditNotes('');
    setEditDate('');
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const updateData = {
        notes: editNotes,
        readDate: new Date(editDate).toISOString()
      };
      
      await updateReadingEntry(editingEntry.id, updateData);
      
      // Update the entry in the local state
      const updatedHistory = readingHistory.map(entry => {
        if (entry.id === editingEntry.id) {
          return {
            ...entry,
            notes: editNotes,
            read_date: editDate
          };
        }
        return entry;
      });
      
      setReadingHistory(updatedHistory);
      setEditingEntry(null);
      setEditNotes('');
      setEditDate('');
      setSuccess('Reading entry updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to update reading entry:', err);
      setError('Failed to update reading entry. Please try again later.');
      setLoading(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this reading entry? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await removeChapterRead(entryId);
      
      // Remove the entry from the local state
      const updatedHistory = readingHistory.filter(entry => entry.id !== entryId);
      setReadingHistory(updatedHistory);
      
      setSuccess('Reading entry deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to delete reading entry:', err);
      setError('Failed to delete reading entry. Please try again later.');
      setLoading(false);
    }
  };

  // Handle view notes button click
  const handleViewNotes = (entry) => {
    setViewingNotes(entry);
  };

  // Handle close notes modal
  const handleCloseNotes = () => {
    setViewingNotes(null);
  };

  // Navigate to the Bible chapter page
  const handleViewChapter = (book, chapter) => {
    navigate(`/bible/${book}/${chapter}`);
  };

  // Render notes modal
  const renderNotesModal = () => {
    if (!viewingNotes) return null;

    return (
      <div className="modal-wrapper">
        <div className="modal-backdrop" onClick={handleCloseNotes}></div>
        <div className="modal show">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Notes for {viewingNotes.book} {viewingNotes.chapter}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseNotes}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">
                  Read on {formatDate(viewingNotes.read_date || viewingNotes.readDate)}
                </p>
                <div className="notes-content">
                  {viewingNotes.notes}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCloseNotes}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => {
                    handleCloseNotes();
                    handleEditClick(viewingNotes);
                  }}
                >
                  Edit Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check if an entry has notes
  const hasNotes = (entry) => {
    return entry.notes && entry.notes.trim().length > 0;
  };

  // Get a preview of notes (truncated if too long)
  const getNotesPreview = (notes, maxLength = 50) => {
    if (!notes) return '-';
    
    if (notes.length <= maxLength) {
      return notes;
    }
    
    return notes.substring(0, maxLength) + '...';
  };

  if (!selectedReader) {
    return (
      <div className="reading-reports">
        <h2>Reading Reports</h2>
        <p>Please select a reader to view their reading reports.</p>
      </div>
    );
  }

  return (
    <div className="reading-reports">
      <h2>Reading Reports</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      
      {loading && !readingHistory.length ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading reading history...</p>
        </div>
      ) : (
        <>
          {readingHistory.length === 0 ? (
            <p>No reading history found for this reader.</p>
          ) : (
            <div className="table-responsive">
              <table className="reading-history-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('book')}>
                      Book {getSortDirectionIndicator('book')}
                    </th>
                    <th onClick={() => handleSort('chapter')}>
                      Chapter {getSortDirectionIndicator('chapter')}
                    </th>
                    <th onClick={() => handleSort('read_date')}>
                      Date Read {getSortDirectionIndicator('read_date')}
                    </th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {readingHistory.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.book}</td>
                      <td>{entry.chapter}</td>
                      <td>
                        {editingEntry && editingEntry.id === entry.id ? (
                          <input
                            type="date"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                            className="form-control"
                          />
                        ) : (
                          formatDate(entry.read_date || entry.readDate)
                        )}
                      </td>
                      <td>
                        {editingEntry && editingEntry.id === entry.id ? (
                          <textarea
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            className="form-control"
                            rows="2"
                          />
                        ) : (
                          <div className="notes-preview">
                            {getNotesPreview(entry.notes)}
                            {hasNotes(entry) && (
                              <button
                                onClick={() => handleViewNotes(entry)}
                                className="btn btn-link btn-sm view-notes-btn"
                                title="View Full Notes"
                              >
                                View Notes
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {editingEntry && editingEntry.id === entry.id ? (
                          <div className="action-buttons">
                            <button
                              onClick={handleSaveEdit}
                              className="btn btn-success btn-sm"
                              disabled={loading}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="btn btn-secondary btn-sm"
                              disabled={loading}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              onClick={() => handleViewChapter(entry.book, entry.chapter)}
                              className="btn btn-primary btn-sm"
                              title="View Chapter"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEditClick(entry)}
                              className="btn btn-warning btn-sm"
                              title="Edit Entry"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(entry.id)}
                              className="btn btn-danger btn-sm"
                              title="Delete Entry"
                              disabled={loading}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      
      {/* Notes Modal */}
      {renderNotesModal()}
    </div>
  );
}

export default ReadingReports; 